import { animate, style, transition, trigger } from "@angular/animations";
import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, signal, SimpleChanges } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { NavigationEnd, Router, RouterLink } from "@angular/router";
import { filter, startWith, takeUntil } from "rxjs";

import { DestroyService } from "@app/core/services/destroy.service";
import { BreadcrumbService } from "@app/shared/components/breadcrumbs/services/breadcrumb.service";
import { IsChildActivePipe } from "@app/shared/components/navbar/is-child-active.pipe";
import { IsParentActivePipe } from "@app/shared/components/navbar/is-parent-active.pipe";
import { ENavbarItemType, TNavigation } from "@app/shared/components/under-ui-header/types/navbar-item.type";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
  selector: "under-ui-navbar",
  templateUrl: "./navbar.component.html",
  styleUrl: "./navbar.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestroyService],
  animations: [
    trigger("expandCollapse", [
      transition(":enter", [
        style({ height: 0, opacity: 0, overflow: "hidden" }),
        animate("200ms ease", style({ height: "*", opacity: 1 })),
      ]),
      transition(":leave", [
        style({ overflow: "hidden" }),
        animate("200ms ease", style({ height: 0, opacity: 0 })),
      ]),
    ]),
  ],
  imports: [
    CommonModule,
    RouterLink,
    MatIconModule,
    TranslatePipe,
    IsChildActivePipe,
    IsParentActivePipe,
  ],
  standalone: true,
})
export class NavbarComponent implements OnInit, OnChanges {
  @Input() buttons: TNavigation[] = [];
  @Input() collapsed = false;

  @Output() onExpandRequest = new EventEmitter<void>();

  readonly ENavbarItemType = ENavbarItemType;

  currentUrl = signal(this.router.url);
  expandedItems = signal<Set<string>>(new Set());
  readonly activeOverride = this.breadcrumbService.activeNavPath;

  private pendingExpandPath: string | null = null;

  constructor(
    private readonly router: Router,
    private readonly destroyService: DestroyService,
    private readonly breadcrumbService: BreadcrumbService,
  ) {}

  ngOnInit(): void {
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      startWith(null),
      takeUntil(this.destroyService),
    ).subscribe(e => {
      this.currentUrl.set(e?.urlAfterRedirects ?? this.router.url);
    });
    this.autoExpandActiveParent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const collapsedChange = changes["collapsed"];
    if (collapsedChange && !collapsedChange.firstChange && collapsedChange.previousValue === true && collapsedChange.currentValue === false) {
      this.autoExpandActiveParent();
      if (this.pendingExpandPath) {
        this.expandedItems.update(set => new Set([...set, this.pendingExpandPath!]));
        this.pendingExpandPath = null;
      }
    }
  }

  onGroupItemClick(path: string): void {
    if (this.collapsed) {
      this.pendingExpandPath = path;
      this.onExpandRequest.emit();
    } else {
      this.toggleItem(path);
    }
  }

  toggleItem(path: string): void {
    this.expandedItems.update(set => {
      const next = new Set(set);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }

  isExpanded(path: string): boolean {
    return this.expandedItems().has(path);
  }

  private autoExpandActiveParent(): void {
    const currentUrl = this.router.url;
    const activeParent = this.buttons.find(
      item => item.type === ENavbarItemType.Group && currentUrl.includes(item.path),
    );
    if (activeParent?.path) {
      this.expandedItems.update(set => new Set([...set, activeParent.path!]));
    }
  }
}
